// src/utils/deliveryUpload.js
// Upload ONE file into a delivery: sign → PUT direct to R2 → record (server HeadObject-verifies before it
// inserts delivery_files). Returns { error } — { error:null } ONLY when the object is provably in R2 AND
// the delivery_files row exists. So a failed upload never leaves a phantom file, and the caller can show
// exactly which files failed and retry only those. Mirrors src/utils/uploadFile.js.
import { supabase } from '../supabase';

export async function deliveryUpload(deliveryId, file) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session && session.access_token;
    if (!token) return { error: new Error('not signed in') };
    const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    const contentType = file.type || 'application/octet-stream';

    // 1. sign — server builds the key under deliveries/<deliveryId>/ and returns it
    const signResp = await fetch('/api/delivery-upload-sign', {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({ delivery_id: deliveryId, file_name: file.name, content_type: contentType }),
    });
    if (!signResp.ok) {
      let reason = String(signResp.status);
      try { const j = await signResp.json(); reason = j.error || reason; } catch { /* non-json */ }
      return { error: new Error(`sign: ${reason}`) };
    }
    const { url, storage_path } = await signResp.json();
    if (!url || !storage_path) return { error: new Error('sign: no url/path') };

    // 2. PUT bytes straight to R2 (Content-Type must match what was signed)
    const putResp = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
    if (!putResp.ok) return { error: new Error(`PUT: ${putResp.status}`) };

    // 3. record — server HeadObject-verifies, then inserts delivery_files (verify-before-commit)
    let recResp;
    try {
      recResp = await fetch('/api/delivery-record', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ delivery_id: deliveryId, storage_path, file_name: file.name, content_type: contentType }),
      });
    } catch (e) { return { error: new Error(`verify: ${e.message}`) }; }
    if (!recResp.ok) {
      let reason = String(recResp.status);
      try { const j = await recResp.json(); reason = j.error || reason; } catch { /* non-json */ }
      return { error: new Error(`not verified in storage: ${reason}`) };
    }

    // Server may report the file was already in this delivery (same name+size) → a skip, not a new file.
    const recJson = await recResp.json().catch(() => ({}));
    if (recJson && recJson.duplicate) return { error: null, duplicate: true };
    return { error: null };
  } catch (e) {
    return { error: e };
  }
}
